/**
 * Login Container - Modal-based authentication
 * UPDATED: Uses new authSlice actions (login/logout instead of loginStart/loginSuccess)
 */

import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab
} from '@mui/material';
import { AppDispatch, RootState } from '../../store/store';
import { login, clearError } from '../../store/slices/authSlice';
import { buildEventsApiUrl } from '../../config/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

interface LoginProps {
  open: boolean;
  onClose: () => void;
  initialTab?: number; // Add optional initialTab prop
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LoginContainer: React.FC<LoginProps> = ({ open, onClose, initialTab = 0 }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const [activeTab, setActiveTab] = useState(initialTab);
  const [verificationMessage, setVerificationMessage] = useState<string | null>(null);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!open || !GOOGLE_CLIENT_ID) return;

    const existingScript = document.querySelector('script[data-google-identity="true"]') as HTMLScriptElement | null;

    const renderGoogleButton = () => {
      const target = document.getElementById(activeTab === 0 ? 'google-login-button' : 'google-signup-button');
      if (!target || !window.google?.accounts?.id) return;

      target.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: { credential?: string }) => {
          if (!response?.credential) return;

          try {
            setGoogleLoading(true);
            dispatch(clearError());
            const resultResponse = await fetch(buildEventsApiUrl('/api/auth/google'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ idToken: response.credential })
            });

            const result = await resultResponse.json();
            if (!resultResponse.ok || !result.success || !result.user || !result.token) {
              throw new Error(result.error || 'Google sign-in failed');
            }

            dispatch(login({ user: result.user, token: result.token }));
            setVerificationMessage(null);
            setVerificationError(null);
            onClose();
            navigate('/');
          } catch (error) {
            alert(error instanceof Error ? error.message : 'Google sign-in failed');
          } finally {
            setGoogleLoading(false);
          }
        }
      });

      window.google.accounts.id.renderButton(target, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: activeTab === 0 ? 'signin_with' : 'signup_with'
      });
    };

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return;
    }

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.googleIdentity = 'true';
      script.onload = renderGoogleButton;
      document.head.appendChild(script);
      return;
    }

    existingScript.addEventListener('load', renderGoogleButton);
    return () => existingScript.removeEventListener('load', renderGoogleButton);
  }, [activeTab, dispatch, navigate, onClose, open]);

  useEffect(() => {
    if (!open) {
      dispatch(clearError());
      setVerificationMessage(null);
      setVerificationError(null);
    }
  }, [dispatch, open]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    dispatch(clearError());
    setVerificationMessage(null);
    setVerificationError(null);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResendVerification = async () => {
    if (!formData.email.trim()) {
      setVerificationError('Enter your email first.');
      return;
    }

    try {
      setVerificationLoading(true);
      setVerificationError(null);
      const response = await fetch(buildEventsApiUrl('/api/auth/resend-verification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim() })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to resend verification email');
      }

      setVerificationMessage(result.message || 'Verification email sent.');
    } catch (error) {
      setVerificationError(error instanceof Error ? error.message : 'Failed to resend verification email');
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      dispatch(clearError());
      const response = await fetch(buildEventsApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password
        })
      });

      const result = await response.json();
      if (!response.ok) {
        if (result?.code === 'EMAIL_NOT_VERIFIED') {
          setVerificationMessage(result.error || 'Please verify your email before logging in.');
          throw new Error(result.error || 'Please verify your email before logging in.');
        }
        throw new Error(result.error || 'Login failed');
      }

      if (!result.success || !result.user || !result.token) {
        throw new Error(result.error || 'Login failed');
      }

      dispatch(login({ user: result.user, token: result.token }));
      console.log('✅ Login successful');
      dispatch(clearError());
      setVerificationMessage(null);
      setVerificationError(null);
      onClose();
      navigate('/');
    } catch (error) {
      console.error('❌ Login error:', error);
      alert(error instanceof Error ? error.message : 'Login failed');
    }
  };

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    try {
      dispatch(clearError());
      const response = await fetch(buildEventsApiUrl('/api/auth/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          password: formData.password,
          userName: formData.email.split('@')[0]
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success || !result.user) {
        throw new Error(result.error || 'Signup failed');
      }

      console.log('✅ Signup successful');
      dispatch(clearError());
      setVerificationError(null);
      setVerificationMessage(result.message || 'Signup successful. Check your email to verify your account.');
      setActiveTab(0);
    } catch (error) {
      console.error('❌ Signup error:', error);
      alert(error instanceof Error ? error.message : 'Signup failed');
    }
  };

  const handleClose = () => {
    dispatch(clearError());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Login" />
            <Tab label="Sign Up" />
          </Tabs>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {verificationMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {verificationMessage}
          </Alert>
        )}

        {verificationError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {verificationError}
          </Alert>
        )}

        {/* Login Tab */}
        <TabPanel value={activeTab} index={0}>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
              autoFocus
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Login'}
            </Button>

            {GOOGLE_CLIENT_ID && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Or continue with Google
                </Typography>
                <Box id="google-login-button" sx={{ minHeight: 44, opacity: googleLoading ? 0.6 : 1 }} />
              </Box>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
Use your real CrowdTrades account email and password
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mt: 1, gap: 1 }}>
              <Button
                component={RouterLink}
                to="/forgot-password"
                variant="text"
                sx={{ p: 0, minWidth: 0 }}
              >
                Forgot password?
              </Button>

              <Button
                variant="text"
                onClick={handleResendVerification}
                disabled={verificationLoading}
                sx={{ p: 0, minWidth: 0 }}
              >
                {verificationLoading ? <CircularProgress size={18} /> : 'Resend verification email'}
              </Button>
            </Box>
          </form>
        </TabPanel>

        {/* Signup Tab */}
        <TabPanel value={activeTab} index={1}>
          <form onSubmit={handleSignup}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ mt: 3, mb: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign Up'}
            </Button>

            {GOOGLE_CLIENT_ID && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Or sign up with Google
                </Typography>
                <Box id="google-signup-button" sx={{ minHeight: 44, opacity: googleLoading ? 0.6 : 1 }} />
              </Box>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
Create a real CrowdTrades account stored in the database. You’ll need to verify your email before logging in.
            </Typography>
          </form>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default LoginContainer;



